import React from 'react';
import { View } from 'react-native';
import { useLazyQuery } from '@apollo/client';
import {
  GET_STYLIST,
  GET_STYLIST_NEXT_AVAILABLE_SLOT,
  GET_STYLIST_SHARE_PROFILE_URL,
} from '@/shared/queries/stylistSchema';
import StylistProfile from '@/shared/components/StylistProfile';
import useStore from '@/stores/useStore';
import useExploreStore from '@/stores/useExploreStore';
import SentryService from '@/services/sentry/Sentry.service';
import {
  formatNameWithLastInitial,
  shouldSurfaceGraphQLError,
} from '@/shared/utils';
import { ApolloError } from '@apollo/client';
import { useEffect } from 'react';
import {
  QueryResult,
  QueryStylistArgs,
  QueryNextAvailableSlotArgs,
  QueryStylistShareProfileUrlArgs,
} from '@/shared/types/generated';
import { FullScreenLoader } from '@/shared/components/FullScreenLoader';
import Modal from '@/shared/components/Modal';
import { Portal } from 'react-native-paper';
import SnackBar from '@/shared/components/SnackBar';

interface Props {
  id: number | null;
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  onConsultationButtonPress?: () => void;
  showConsultationButton?: boolean;
  onDismiss?: () => void;
}

const StylistProfileModal = ({
  id,
  isVisible,
  setIsVisible,
  onConsultationButtonPress,
  showConsultationButton = true,
  onDismiss,
}: Props) => {
  const snackbarState = useStore((state) => state.snackbarState);
  const setSnackbarState = useStore((state) => state.setSnackbarState);
  const clearSnackBar = useStore((state) => state.clearSnackBar);
  const { setSelectedStylist, selectedStylist } = useExploreStore();

  const [getData, { loading }] = useLazyQuery<
    QueryResult<'stylist'>,
    QueryStylistArgs
  >(GET_STYLIST, {
    onError: (error) => {
      SentryService.captureError('Error fetching stylist profile', error);
      if (error instanceof ApolloError) {
        setSnackbarState({
          open: true,
          message: shouldSurfaceGraphQLError(error)
            ? error.graphQLErrors?.[0]?.message
            : 'Unable to load stylist profile. Please try again later.',
          severity: 'error',
        });
      }
    },
    onCompleted: (data) => {
      if (data?.stylist) {
        setSelectedStylist(data.stylist);
      }
    },
  });

  const [getStylistNextAvailableSlot, { data: nextAvailableSlotData }] =
    useLazyQuery<QueryResult<'nextAvailableSlot'>, QueryNextAvailableSlotArgs>(
      GET_STYLIST_NEXT_AVAILABLE_SLOT,
      {
        fetchPolicy: 'network-only',
        onError: (error) => {
          SentryService.captureError(
            `Error fetching next available slot for stylist ${id}`,
            error,
          );
        },
      },
    );

  const [getShareProfileUrl, { data: shareProfileUrlData }] = useLazyQuery<
    QueryResult<'stylistShareProfileUrl'>,
    QueryStylistShareProfileUrlArgs
  >(GET_STYLIST_SHARE_PROFILE_URL, {
    onError: (error) => {
      SentryService.captureError(
        `Error fetching share profile url for stylist ${id}`,
        error,
      );
    },
  });

  useEffect(() => {
    if (id && isVisible) {
      getData({ variables: { input: { id } } });

      // Fetch next available slot
      getStylistNextAvailableSlot({
        variables: {
          input: {
            stylistId: id,
            currentDate: new Date().toISOString(),
          },
        },
      });

      getShareProfileUrl({
        variables: { input: { stylistId: id } },
      });
    }
  }, [id, isVisible, getData, getStylistNextAvailableSlot, getShareProfileUrl]);

  if (loading) {
    return <FullScreenLoader />;
  }
  if (!selectedStylist) {
    return null;
  }

  const stylist = selectedStylist;
  const stylistName = formatNameWithLastInitial(stylist.user).toUpperCase();

  const handleModalClose = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  return (
    <Modal
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      onDismiss={onDismiss}
      onRequestClose={onDismiss}
    >
      <Portal.Host>
        <View style={{ flex: 1 }}>
          <StylistProfile
            stylistName={stylistName}
            expertise={stylist.expertise}
            aesthetics={stylist.aesthetics}
            profilePicUrl={stylist.user?.profilePicUrl ?? ''}
            instagramHandle={stylist.instagram}
            tiktokHandle={stylist.tiktok}
            substackHandle={stylist.substack}
            bio={stylist.bio}
            landscapeImage={stylist.landscapeImage ?? null}
            portraitImages={stylist.portraitImages ?? []}
            showConsultationButton={showConsultationButton}
            onClose={handleModalClose}
            pricing={stylist?.pricing ?? undefined}
            customInPersonHourlyRate={
              stylist?.customInPersonHourlyRate ?? undefined
            }
            isAvailableInPerson={stylist?.computedInPersonAvailability ?? false}
            customHourlyRate={stylist?.customHourlyRate ?? undefined}
            onConsultationButtonPress={onConsultationButtonPress}
            nextAvailableSlot={nextAvailableSlotData?.nextAvailableSlot}
            shareProfileUrl={
              shareProfileUrlData?.stylistShareProfileUrl?.shareProfileUrl ?? ''
            }
            isModalView
          />
        </View>
        <SnackBar
          visible={snackbarState.open}
          onDismiss={clearSnackBar}
          message={snackbarState.message}
          severity={snackbarState.severity}
          hasNavBar={false}
        />
      </Portal.Host>
    </Modal>
  );
};

export default StylistProfileModal;
